// src/store/cashier.store.ts
import { create } from "zustand";
import { persist } from "zustand/middleware";
import { CashierOrderDraft, OrderType, CartItem } from "../types";

interface CashierState {
  pipelineOrders: CashierOrderDraft[];
  activeDraftId?: string | null;
  cashierPhone?: string | null;
  cashierUid?: string | null;

  // Actions
  createDraft: (params: {
    orderType: OrderType;
    customerPhone?: string | null;
    customerName?: string | null;
  }) => CashierOrderDraft | null;

  setActiveDraft: (draftId: string | null) => void;
  updateDraftItems: (draftId: string, items: CartItem[], totalAmount: number) => void;
  updateDraftMeta: (
    draftId: string,
    data: Partial<Pick<CashierOrderDraft, "orderType" | "customerPhone" | "customerName">>
  ) => void;
  removeDraft: (draftId: string) => void;
  clearAllDrafts: () => void;
  setCashierInfo: (phone: string, uid: string) => void;
  getPipelineCount: () => number;
}

export const useCashierStore = create<CashierState>()(
  persist(
    (set, get) => ({
      pipelineOrders: [],
      activeDraftId: null,
      cashierPhone: null,
      cashierUid: null,

      setCashierInfo: (phone, uid) => {
        set({ cashierPhone: phone, cashierUid: uid });
      },

      createDraft: ({ orderType, customerPhone, customerName }) => {
        const { pipelineOrders } = get();
        
        if (pipelineOrders.length >= 5) {
          console.warn("[Cashier] Max 5 orders in pipeline reached");
          return null;
        }

        const now = new Date();
        const id = `draft_${now.getTime()}`;

        const draft: CashierOrderDraft = {
          id,
          orderType,
          customerPhone: customerPhone || null,
          customerName: customerName || null,
          items: [],
          totalAmount: 0,
          createdAt: now,
        };

        set({
          pipelineOrders: [...pipelineOrders, draft],
          activeDraftId: id,
        });

        return draft;
      },

      setActiveDraft: (draftId) => {
        set({ activeDraftId: draftId });
      },

      updateDraftItems: (draftId, items, totalAmount) => {
        set((state) => ({
          pipelineOrders: state.pipelineOrders.map((d) =>
            d.id === draftId ? { ...d, items, totalAmount } : d
          ),
        }));
      },

      updateDraftMeta: (draftId, data) => {
        set((state) => ({
          pipelineOrders: state.pipelineOrders.map((d) =>
            d.id === draftId ? { ...d, ...data } : d
          ),
        }));
      },

      removeDraft: (draftId) => {
        set((state) => ({
          pipelineOrders: state.pipelineOrders.filter((d) => d.id !== draftId),
          activeDraftId:
            state.activeDraftId === draftId ? null : state.activeDraftId,
        }));
      },

      clearAllDrafts: () => {
        set({ pipelineOrders: [], activeDraftId: null });
      },

      getPipelineCount: () => {
        return get().pipelineOrders.length;
      },
    }),
    {
      name: "ruchira-cashier-pipeline",
    }
  )
);
